import { useRoute } from "@/components/shared/router";
import { Typography } from "@/components/shared/typography";
import { Image, View } from "@/components/ui";
import { IconButton } from "@/components/ui/icon-button";
import { formatDate } from "@/lib/helpers/date";

import { formatGiftCardClientNameHide, getGiftCardOccasionIcon } from "./lib";
import type { GiftCardHistoryItem as GiftCardHistoryItemType } from "./types";
import { GiftCardStatus } from "./types";

interface GiftCardHistoryItemReceivedProps {
  item: GiftCardHistoryItemType;
}

export const GiftCardHistoryItemReceived = ({ item }: GiftCardHistoryItemReceivedProps) => {
  const { push } = useRoute();
  const icon = getGiftCardOccasionIcon(item.occasion.key);
  const isRedeemed = item.status === GiftCardStatus.Redeemed;

  const getTitle = () => {
    return isRedeemed ? "Gift Card Redeemed" : "Gift Card Expired";
  };

  const getSubtitle = () => {
    const nameWithAsterisk = formatGiftCardClientNameHide(item.senderName);
    const prefix = "From";
    return `${prefix} ${nameWithAsterisk}`;
  };

  const handlePress = () => {
    push({
      pageKey: "giftCardDetail",
      params: { id: item.id },
    });
  };

  return (
    <View className="flex flex-col p-4" onClick={handlePress}>
      <View className="flex flex-row items-start gap-3">
        <Image src={icon} width={32} height={32} className="size-8" />
        <View className="flex flex-1 flex-col gap-1">
          <Typography variant="label-sm">{getTitle()}</Typography>
          <Typography variant="body-sm">{getSubtitle()}</Typography>
          {isRedeemed && item.expiresAt && (
            <Typography variant="body-sm" color="foreground-intermediate">
              Expires: {formatDate(new Date(item.expiresAt), "d MMM yyyy", true)}
            </Typography>
          )}
          <Typography variant="caption-md">
            {formatDate(new Date(item.createdAt), "d MMM yyyy, hh:mm a", true)}
          </Typography>
        </View>
        <View className="self-center">
          <View className="flex flex-row items-center gap-1">
            <Typography variant="body-sm" color={isRedeemed ? "success" : "danger"}>
              {isRedeemed ? "+" : "-"}${item.amount}
            </Typography>
            <IconButton
              variant="ghost"
              color="foreground"
              iconName="caretRight"
              size="sm"
              className="p-0"
              onClick={handlePress}
            />
          </View>
        </View>
      </View>
    </View>
  );
};
